import React, { useState, useEffect, useRef } from 'react';
import { registerUser } from "../../../../src/services/emailRegister/emailRegisterServices.js";
import axios from "axios";
import { toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import { Link, useNavigate, useParams } from "react-router-dom";
import EyeIcon from "../../../images/icons/eye-icon.svg";
import EyeCrossed from "../../../images/icons/eye-crossed.svg";
import { DatePicker } from "rsuite";
import subDays from "date-fns/subDays";
import CalendarIcon from '../../../components/ui/CalendarIcon';
import "../../../components/Onboarding/OnboardingCompanyDetails/CompanyDetailsSidebar.css";
import "../../../components/Onboarding/OnboardingCompanyDetails/CompanyDetaislForm.css";
import "../../onboarding/registeration/OnboardingCompanyDetails.css";
import { getBrandLogo, getBrandLogoAlt } from "../../../utils/brandLogo";

const STEP_COMPANY = 1;
const STEP_ACCOUNT = 2;
const STEP_PERSONAL = 3;
const STEP_SECURITY = 4;
const STEP_SUCCESS = 5;

const SIDEBAR_STEPS = [
    { label: "Company details", desc: "Please provide your company details." },
    { label: "Company account information", desc: "Please provide your company account details." },
    { label: "Your personal details", desc: "Please provide your profile details." },
    { label: "Security", desc: "Select your security preferences." },
];

const ranges = [
    { label: "today", value: new Date() },
    { label: "yesterday", value: subDays(new Date(), 1) },
];

const TenantInviteRegister = () => {
    const { email } = useParams();
    const deEmail = decodeURIComponent(
        atob(email.replace(/-/g, "+").replace(/_/g, "/"))
    );
    const hasVerified = useRef(false);
    const navigate = useNavigate();
    const passportFileRef = useRef(null);

    const [step, setStep] = useState(STEP_COMPANY);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordMatchError, setPasswordMatchError] = useState("");

    // Step 1: Company Details
    const [companyData, setCompanyData] = useState({
        commercialName: "",
        legalEntityName: "",
        taxId: "",
    });

    // Step 2: Company Account Information
    const [accountData, setAccountData] = useState({
        address: "",
        city: "",
        country: "",
        postalCode: "",
        mobileNo: "",
    });

    // Step 3: Personal Details
    const [personalData, setPersonalData] = useState({
        firstName: "",
        lastName: "",
        idNumber: "",
        address: "",
        city: "",
        country: "",
        postalCode: "",
        phoneNo: "",
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [passportFileName, setPassportFileName] = useState("");

    // Step 4: Security
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // Registered tenant subdomain for redirect
    const [tenantSubDomain, setTenantSubDomain] = useState("");

    const domainName = window.location.hostname;
    const currentPort = window.location.port;

    // Verify invite link on mount
    useEffect(() => {
        const verifyLinkValidity = async () => {
            try {
                const response = await axios.post("/api/tenant/check/user-invite/validity", { email: deEmail });
                if (response.data.status === "emailAlreadyRegistered") {
                    toast.error("This email is already registered!", { position: "bottom-right", autoClose: 2000, hideProgressBar: true, theme: "light" });
                    navigate("/");
                }
                if (response.data.status === "linkExpired") {
                    toast.error("Sorry! Invite link expired", { position: "bottom-right", autoClose: 2000, hideProgressBar: true, theme: "light" });
                    navigate("/");
                }
            } catch (error) {
                console.error(error);
                global.config.slackMessage(error.toString());
                toast.error("Something went wrong while verifying the invite link");
            }
        };

        if (deEmail && !hasVerified.current) {
            hasVerified.current = true;
            verifyLinkValidity();
        }
    }, [deEmail, navigate]);

    const handleCompanySubmit = (e) => {
        e.preventDefault();
        setStep(STEP_ACCOUNT);
    };

    const handleAccountSubmit = (e) => {
        e.preventDefault();
        setStep(STEP_PERSONAL);
    };

    const handleDateChange = (date) => {
        if (date > new Date()) {
            toast.error("Date of Birth cannot be in the future", { position: "bottom-right", autoClose: 3000, theme: "light" });
            return;
        }
        setSelectedDate(date);
    };

    const handlePassportFileChange = (e) => {
        const file = e.target.files[0];
        if (file) setPassportFileName(file.name);
    };

    const handlePersonalSubmit = (e) => {
        e.preventDefault();
        if (!selectedDate) {
            toast.error("Please select Date of Birth", { position: "bottom-right", autoClose: 1500, theme: "light" });
            return;
        }
        setStep(STEP_SECURITY);
    };

    const handleSecuritySubmit = async (e) => {
        e.preventDefault();
        setPasswordMatchError("");

        if (password !== confirmPassword) {
            setPasswordMatchError("Passwords do not match");
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-./;:<=>?@[\\\]^`{|}~])[A-Za-z\d!@#$%^&*()_+\-./;:<=>?@[\\\]^`{|}~]{8,}$/;
        if (!passwordRegex.test(password)) {
            toast.error("Password must be at least 8 characters and include uppercase, lowercase, digit, and special character", {
                position: "bottom-right", autoClose: 3800, hideProgressBar: true, theme: "light",
            });
            return;
        }

        setLoading(true);
        try {
            const browserTimeZone = Intl.DateTimeFormat("en-US").resolvedOptions().timeZone;
            const result = await registerUser(deEmail, companyData.commercialName, password, companyData.commercialName, false, browserTimeZone);
            if (result.status === "success") {
                if (result.data && result.data.subDomain) {
                    setTenantSubDomain(result.data.subDomain);
                }
                setStep(STEP_SUCCESS);
            } else {
                toast.error(result.message || "Something went wrong.", { position: "bottom-right", autoClose: 2000, hideProgressBar: true, theme: "light" });
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Something went wrong while registering.", { position: "bottom-right", autoClose: 2000, hideProgressBar: true, theme: "light" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="onboarding-page-outer" style={{ overflow: "hidden", height: "100vh" }}>
            {/* Sidebar */}
            <div className="onboarding-sidebar-outer">
                <div className="onboard-logo">
                    <Link to="/"><img src={getBrandLogo()} alt="Talenton" /></Link>
                </div>
                <div className="sidebar-contents">
                    {SIDEBAR_STEPS.map((s, i) => {
                        const stepNum = i + 1;
                        const isActive = step === stepNum;
                        const isActivated = step > stepNum;
                        return (
                            <div
                                key={stepNum}
                                className={`sidebar-contents-inner${isActive ? " active" : ""}${isActivated ? " activated" : ""}`}
                            >
                                <div className="sidebar-single-point">{s.label}</div>
                                <div className="sidebar-single-point-content">{s.desc}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Form area */}
            <div className="detail-form-section-outer" style={{ overflowY: "auto", height: "100vh", direction: "rtl" }}>
                <div className="onboarding-detail-outer" style={{ direction: "ltr" }}>

                    {/* Step 1: Company Details */}
                    {step === STEP_COMPANY && (
                        <>
                            <div className="company-detail-title">Company Details</div>
                            <form onSubmit={handleCompanySubmit}>
                                <div className="form-group">
                                    <label htmlFor="commercialName">Commercial Name*</label>
                                    <input
                                        type="text"
                                        id="commercialName"
                                        value={companyData.commercialName}
                                        onChange={(e) => setCompanyData({ ...companyData, commercialName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="legalEntityName">Legal Entity Name*</label>
                                    <input
                                        type="text"
                                        id="legalEntityName"
                                        value={companyData.legalEntityName}
                                        onChange={(e) => setCompanyData({ ...companyData, legalEntityName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="emailStep1">Email*</label>
                                    <input
                                        type="email"
                                        id="emailStep1"
                                        value={deEmail}
                                        readOnly
                                        className="read-only-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="taxId">Tax ID number*</label>
                                    <input
                                        type="text"
                                        id="taxId"
                                        value={companyData.taxId}
                                        onChange={(e) => setCompanyData({ ...companyData, taxId: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="continue-button">
                                    <button type="submit">Continue</button>
                                </div>
                            </form>
                        </>
                    )}

                    {/* Step 2: Company Account Information */}
                    {step === STEP_ACCOUNT && (
                        <>
                            <div className="company-detail-title">Company Account information</div>
                            <form onSubmit={handleAccountSubmit}>
                                <div className="form-group">
                                    <label htmlFor="accAddress">Address</label>
                                    <input
                                        type="text"
                                        id="accAddress"
                                        value={accountData.address}
                                        onChange={(e) => setAccountData({ ...accountData, address: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="accCity">City</label>
                                    <input
                                        type="text"
                                        id="accCity"
                                        value={accountData.city}
                                        onChange={(e) => setAccountData({ ...accountData, city: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="accCountry">Country</label>
                                    <input
                                        type="text"
                                        id="accCountry"
                                        value={accountData.country}
                                        onChange={(e) => setAccountData({ ...accountData, country: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="accPostalCode">Postal Code</label>
                                    <input
                                        type="text"
                                        id="accPostalCode"
                                        value={accountData.postalCode}
                                        onChange={(e) => setAccountData({ ...accountData, postalCode: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="accMobileNo">Mobile No.</label>
                                    <input
                                        type="text"
                                        id="accMobileNo"
                                        value={accountData.mobileNo}
                                        onChange={(e) => setAccountData({ ...accountData, mobileNo: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-button-group">
                                    <div className="form-back-button">
                                        <button type="button" onClick={() => setStep(STEP_COMPANY)}>Back</button>
                                    </div>
                                    <div className="continue-button">
                                        <button type="submit">Continue</button>
                                    </div>
                                </div>
                            </form>
                        </>
                    )}

                    {/* Step 3: Personal Details */}
                    {step === STEP_PERSONAL && (
                        <>
                            <div className="company-detail-title">Your personal details</div>
                            <div className="company-detail-sub-title">
                                You will be the company account's super admin. Please fill out your personal information.
                            </div>
                            <form onSubmit={handlePersonalSubmit}>
                                <div className="form-group">
                                    <label htmlFor="firstName">First Name</label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        value={personalData.firstName}
                                        onChange={(e) => setPersonalData({ ...personalData, firstName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="lastName">Last Name</label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        value={personalData.lastName}
                                        onChange={(e) => setPersonalData({ ...personalData, lastName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="emailStep3">Email</label>
                                    <input
                                        type="email"
                                        id="emailStep3"
                                        value={deEmail}
                                        readOnly
                                        className="read-only-input"
                                    />
                                </div>
                                <div className="form-group date-of-birth">
                                    <label>Date of Birth</label>
                                </div>
                                <div className="date-picker-outer date-picker-outer-all">
                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                        <DatePicker
                                            value={selectedDate ? new Date(selectedDate) : null}
                                            oneTap
                                            format="dd/MM/yyyy"
                                            ranges={ranges}
                                            onChange={handleDateChange}
                                            placement="auto"
                                            className="invoice-filter-top-date"
                                            caretAs={CalendarIcon}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="idNumber">Passport or ID Number</label>
                                    <div className="id-brwosing">
                                        <div className="id-detail">
                                            <input
                                                type="text"
                                                id="idNumber"
                                                value={personalData.idNumber}
                                                onChange={(e) => setPersonalData({ ...personalData, idNumber: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="browsing-outer">
                                            <input
                                                type="file"
                                                ref={passportFileRef}
                                                style={{ display: "none" }}
                                                onChange={handlePassportFileChange}
                                                accept="image/*,.pdf"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => passportFileRef.current && passportFileRef.current.click()}
                                                style={{
                                                    marginTop: "9px",
                                                    padding: "10.5px 14px",
                                                    border: "0.5px solid rgba(106, 121, 136, 0.70)",
                                                    borderRadius: "7px",
                                                    background: "#F5F6F8",
                                                    color: "#282828",
                                                    fontSize: "14px",
                                                    fontWeight: "400",
                                                    cursor: "pointer",
                                                    whiteSpace: "nowrap",
                                                    lineHeight: "1",
                                                }}
                                            >
                                                + Browse
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="perAddress">Address</label>
                                    <input
                                        type="text"
                                        id="perAddress"
                                        value={personalData.address}
                                        onChange={(e) => setPersonalData({ ...personalData, address: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="perCity">City</label>
                                    <input
                                        type="text"
                                        id="perCity"
                                        value={personalData.city}
                                        onChange={(e) => setPersonalData({ ...personalData, city: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="perCountry">Country</label>
                                    <input
                                        type="text"
                                        id="perCountry"
                                        value={personalData.country}
                                        onChange={(e) => setPersonalData({ ...personalData, country: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="perPostalCode">Postal Code</label>
                                    <input
                                        type="text"
                                        id="perPostalCode"
                                        value={personalData.postalCode}
                                        onChange={(e) => setPersonalData({ ...personalData, postalCode: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="perPhoneNo">Phone No.</label>
                                    <input
                                        type="text"
                                        id="perPhoneNo"
                                        value={personalData.phoneNo}
                                        onChange={(e) => setPersonalData({ ...personalData, phoneNo: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-button-group">
                                    <div className="form-back-button">
                                        <button type="button" onClick={() => setStep(STEP_ACCOUNT)}>Back</button>
                                    </div>
                                    <div className="continue-button">
                                        <button type="submit">Continue</button>
                                    </div>
                                </div>
                            </form>
                        </>
                    )}

                    {/* Step 4: Security */}
                    {step === STEP_SECURITY && (
                        <>
                            <div className="company-detail-title">Security</div>
                            <form onSubmit={handleSecuritySubmit}>
                                <div className="form-group">
                                    <label htmlFor="emailSec">Email address*</label>
                                    <input
                                        type="email"
                                        id="emailSec"
                                        value={deEmail}
                                        disabled
                                        className="read-only-input"
                                    />
                                </div>
                                <div className="form-group password">
                                    <label htmlFor="password">Password*</label>
                                    <div className="password-input-wrapper">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            placeholder="Password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                        <img
                                            src={showPassword ? EyeCrossed : EyeIcon}
                                            className="password-toggle-icon"
                                            alt="Toggle Password"
                                            onClick={() => setShowPassword(!showPassword)}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="confirmPassword">Confirm Password*</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        placeholder="Confirm Password"
                                        value={confirmPassword}
                                        onChange={(e) => { setConfirmPassword(e.target.value); setPasswordMatchError(""); }}
                                        className={passwordMatchError ? "error" : ""}
                                        required
                                    />
                                    {passwordMatchError && (
                                        <span style={{ color: "red", fontSize: "13px" }}>{passwordMatchError}</span>
                                    )}
                                </div>
                                <div className="form-button-group">
                                    <div className="form-back-button">
                                        <button type="button" onClick={() => setStep(STEP_PERSONAL)}>Back</button>
                                    </div>
                                    <div className="continue-button">
                                        <button type="submit" disabled={loading}>
                                            {loading ? (
                                                <div className="outter-load-table">
                                                    <ThreeDots height="20" width="40" radius="4" color="white" visible={true} />
                                                </div>
                                            ) : "Complete onboarding"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </>
                    )}

                    {/* Step 5: Success */}
                    {step === STEP_SUCCESS && (
                        <>
                            <div className="company-detail-title">You're all set!</div>
                            <p className="thank-you-form-submit">
                                Thank you for registering! Your account has been created. To access your Talenton account, please log in using your credentials.
                            </p>
                            <div className="continue-button">
                                <a
                                    href={
                                        tenantSubDomain
                                            ? currentPort === "3000"
                                                ? `http://${tenantSubDomain}.${domainName}:3000`
                                                : `https://${tenantSubDomain}.${domainName}`
                                            : "/"
                                    }
                                >
                                    Go to my account
                                </a>
                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
};

export default TenantInviteRegister;
